<template>
    <tr style="cursor: pointer">
        <td class="py-2">
            <v-img
                width="20"
                :src="icons[asset.kind]"
                alt="" />
        </td>

        <td>
            <div
                v-if="assetLoaded"
                class="py-2 d-flex align-center">
                <div>
                    <app-storage-image
                        :storage-key="asset.images.mainImage"
                        class="mr-3"
                        :aspect-ratio="4/3"
                        width="60" />
                </div>

                <div class="d-flex flex-column">
                    <a target="_blank">{{ address.firstLine }}</a>
                    <a target="_blank">{{ address.secondLine }}</a>
                </div>
            </div>
        </td>

        <td>
            {{ saleTitle }}
        </td>

        <td
            :class="{ 'text-red': participation.bid.status === 'Pending',
                      'font-weight-bold': isCurrent }">
            <div class="d-flex flex-column">
                {{ participation.bid.bidValue | currency }}
            </div>
        </td>

        <td
            :class="{ 'text-red': participation.bid.status === 'Pending',
                      'font-weight-bold': isCurrent }">
            {{ isCurrent ? 'Current Bid' : participation.bid.status }}
        </td>

        <td>
            {{ timeLeft || '-' }}
        </td>
    </tr>
</template>

<script>
import moment from 'moment';
import { Utils } from '@/utils';
import AppStorageImage from '@/components/App/AppStorageImage';
import subscriptions from '@/api/subscriptions';

export default {
    name: 'PersonalInfoMyBidsRow',

    components: { AppStorageImage },

    props: {
        participation: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            address: {
                firstLine: '',
                secondLine: '',
            },
            asset: {},
            timeLeft: null,
            assetLoaded: false,
            icons: {
                Residential: require('@/assets/images/asset-icons/residential.svg'),
                Commercial: require('@/assets/images/asset-icons/commercial.svg'),
                Land: require('@/assets/images/asset-icons/land.svg'),
            },
        };
    },

    computed: {
        isCurrent() {
            return this.participation.bid.id === this.participation.sale.englishAuction.currentBidID;
        },
        saleTitle() {
            return Utils.camelCaseToTitle(this.participation.sale.kind);
        },
    },

    created() {
        this.subscribeOnChanges();

        this.asset = this.participation.asset;
        this.formatAddress();
        this.timeLeft = this.calculateCountdown();

        const interval = setInterval(() => {
            this.timeLeft = this.calculateCountdown();
        }, 1000);

        this.$once('hook:beforeDestroy', () => {
            clearInterval(interval);
        });

        this.assetLoaded = true;
    },

    methods: {
        formatAddress() {
            this.address = {
                firstLine: this.asset.address.address,
                secondLine: `${this.asset.address.city}, ${this.asset.address.state}`,
            };

            if (this.asset.address.zip) {
                this.address.secondLine += ', ' + this.asset.address.zip;
            }
        },

        calculateCountdown() {
            const syncDiff = this.$store.state.timeDiff || 0;
            const serverTime = moment.unix(moment().unix() + syncDiff).unix();
            const endTime = moment(this.participation.sale.englishAuction.endDate).unix();

            if (endTime - serverTime < 0) {
                return '00:00:00';
            }

            const duration = moment.duration((endTime - serverTime) * 1000, 'milliseconds');

            const days = Math.floor(duration.asMilliseconds() / 1000 / 60 / 60 / 24);
            const hours = duration.hours().toString().padStart(2, '0');
            const minutes = duration.minutes().toString().padStart(2, '0');
            const seconds = duration.seconds().toString().padStart(2, '0');

            return `${days ? days + 'd ' : ''}${hours}:${minutes}:${seconds}`;
        },

        subscribeOnChanges() {
            const onSetCurrentBidSub = subscriptions.onSetCurrentBid(this.participation.sale.id).subscribe(({ value }) => {
                const currentBidID = value.data.onSetCurrentBid[this.participation.sale.kind].currentBidID;
                this.$emit('current-bid-id-update', currentBidID);
            });

            const onEditAuctionSub = subscriptions
                .onEditAuction(this.participation.sale.id)
                .subscribe(({ englishAuction }) => {
                    if (englishAuction?.endDate) {
                        this.$emit('end-date-update', englishAuction.endDate);
                    }
                });

            this.$once('hook:beforeDestroy', () => {
                onSetCurrentBidSub.unsubscribe();
                onEditAuctionSub.unsubscribe();
            });
        },
    },
};
</script>

<style scoped lang="scss">
td {
    font-size: 12px !important;
}
</style>
